import { useMemo, useCallback } from 'react';
import { useI18n } from '../context/I18nContext';
import { useAuth } from '../context/AuthContext';
import { useCategories } from './useCategories';
import { categoriesService } from '../services/supabase';
import { getTranslatedCategoryLabel, isEmailCategory } from '../utils/categoryTranslations';

export type CategorySelectType = 'contact_type' | 'sector';

/** Options for SelectWithAdd: value, label, category (for base/custom display). */
export interface CategorySelectOption {
  value: string;
  label: string;
  category: { id: number; user_id: string | null };
}

/** Base "Other" contact type category (seed id 1, user_id null). Used for migration on delete. */
function getOtherContactTypeCategory(categories: { type: string; user_id?: string | null; id: number }[]) {
  return categories.find(c => c.type === 'contact_type' && c.user_id == null && c.id === 1) ?? null;
}

export function useCategorySelect(type: CategorySelectType) {
  const { user } = useAuth();
  const { t, language } = useI18n();
  const { categories, loading, createCategory, deleteCategory, refresh } = useCategories(type);

  const defaultCategory = useMemo(() => {
    const list = categories.filter(c => c.type === type);
    if (type === 'contact_type') {
      const email = list.find(c => isEmailCategory(c));
      return email ?? list[0] ?? null;
    }
    return null; // sector: no default, user must choose or leave empty
  }, [categories, type]);

  /** When the selected category is deleted, use this for contact_type (Other) or null for sector (empty). */
  const replacementCategoryWhenDeleted = useMemo(() => {
    if (type !== 'contact_type') return null;
    return getOtherContactTypeCategory(categories);
  }, [categories, type]);

  const options: CategorySelectOption[] = useMemo(() => {
    return categories
      .filter(cat => cat.type === type)
      .sort((a, b) => {
        const pa = a.priority ?? 999;
        const pb = b.priority ?? 999;
        if (pa !== pb) return pa - pb;
        if (a.user_id === null && b.user_id === null) return a.id - b.id;
        if (a.user_id === null) return -1;
        if (b.user_id === null) return 1;
        return getTranslatedCategoryLabel(a, t, language).localeCompare(getTranslatedCategoryLabel(b, t, language));
      })
      .map(cat => ({
        value: cat.id.toString(),
        label: getTranslatedCategoryLabel(cat, t, language),
        category: { id: cat.id, user_id: cat.user_id ?? null },
      }));
  }, [categories, type, t, language]);

  const handleCreate = useCallback(
    async (label: string) => {
      const trimmed = label.trim();
      const newCategory = await createCategory({
        type,
        label_fr: trimmed,
        label_en: trimmed,
        user_id: user?.id,
      });
      return newCategory;
    },
    [type, createCategory, user?.id]
  );

  const countUsed = useCallback(
    (categoryId: number) => categoriesService.countLeadsUsingCategory(categoryId, type),
    [type]
  );

  const performDeleteWithMigrate = useCallback(
    async (categoryId: number): Promise<void> => {
      const count = await categoriesService.countLeadsUsingCategory(categoryId, type);
      if (count > 0) {
        if (type === 'contact_type') {
          const other = getOtherContactTypeCategory(categories);
          if (!other) throw new Error('Other category not found');
          await categoriesService.updateLeadsToOtherCategory(categoryId, other.id);
        } else {
          await categoriesService.updateLeadsSectorToNull(categoryId);
        }
      }
      await deleteCategory(categoryId);
      await refresh();
    },
    [type, categories, deleteCategory, refresh]
  );

  const isCustomOption = useCallback(
    (value: string): boolean => {
      const category = categories.find(c => c.id.toString() === value);
      return Boolean(category?.user_id !== null && category?.user_id === user?.id);
    },
    [categories, user?.id]
  );

  return {
    options,
    loading,
    defaultCategory,
    /** Id to use when the selected category is deleted: Other (contact_type) or empty (sector). */
    replacementCategoryWhenDeleted,
    createCategory: handleCreate,
    countUsed,
    performDeleteWithMigrate,
    isCustomOption,
    placeholderLabel:
      type === 'sector'
        ? t('leads.chooseSector')
        : defaultCategory
          ? getTranslatedCategoryLabel(defaultCategory, t, language)
          : '',
  };
}
